import { DndContext, DragOverlay } from "@dnd-kit/core";
import { AddableCourse, CourseLite, CourseSummary } from "../../types/course.response";
import { Requirement, CourseSet, Select } from "../../types/requirement.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { useCourseSetDnd } from "../../hooks/useCourseSetDnd";
import SidebarCourseSetSlots from "./SidebarCourseSetSlots";
import SidebarGroupSlots from "./SidebarGroupSlots";
import SidebarOptionSlots from "./SidebarOptionSlots";
import SidebarOptionGroupSlots from "./SidebarOptionGroupSlots";
import RemoveDropZone from "../shared/RemoveDropZone";

interface SidebarCompletedCoursesProps {
    requirement: Requirement;
    coursesMap: Record<string, CourseSummary>;
    onAdd: (course: AddableCourse, nodeId: string | null) => void | Promise<void>;
    onDelete: (course: CourseLite) => void | Promise<void>;
    onOverride: (course: CourseLite, override: CourseRequirementOverride) => void | Promise<void>;
}

export default function SidebarCompletedCourses({
    requirement,
    coursesMap,
    onAdd,
    onDelete,
    onOverride,
}: SidebarCompletedCoursesProps) {
    const { activeDrag, handleDragStart, handleDragEnd, handleDragCancel } = useCourseSetDnd(requirement, onOverride);

    const renderSlots = () => {
        switch (requirement.info.uiType) {
            case "LIST": {
                const courseSet = requirement.rootNode as CourseSet;
                return (
                    <div className="mt-2 flex flex-col gap-0.5">
                        <SidebarCourseSetSlots
                            courseSet={courseSet}
                            coursesMap={coursesMap}
                            requirementId={requirement.info.id}
                            onAdd={(course) => onAdd(course, courseSet.id)}
                            onAddExtra={(course) => onAdd(course, null)}
                            onDelete={onDelete}
                            idPrefix="sidebar-list"
                            isBroad={requirement.info.isBroadRequirement}
                        />
                    </div>
                );
            }
            case "GROUP":
                return (
                    <SidebarGroupSlots
                        rootNode={requirement.rootNode as Select}
                        requirement={requirement}
                        coursesMap={coursesMap}
                        onAdd={onAdd}
                        onDelete={onDelete}
                        isBroad={requirement.info.isBroadRequirement}
                    />
                );
            case "OPTION":
                return (
                    <SidebarOptionSlots
                        rootNode={requirement.rootNode as Select}
                        requirement={requirement}
                        coursesMap={coursesMap}
                        onAdd={onAdd}
                        onDelete={onDelete}
                        isBroad={requirement.info.isBroadRequirement}
                    />
                );
            case "OPTION_GROUP":
                return (
                    <SidebarOptionGroupSlots
                        rootNode={requirement.rootNode as Select}
                        requirement={requirement}
                        coursesMap={coursesMap}
                        onAdd={onAdd}
                        onDelete={onDelete}
                        isBroad={requirement.info.isBroadRequirement}
                    />
                );
        }
    };

    return (
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
            {renderSlots()}
            <RemoveDropZone
                id={`sidebar-remove-${requirement.info.id}`}
                isVisible={activeDrag?.isApplied === true}
                layout="vertical"
            />
            <DragOverlay>
                {activeDrag ? (
                    <div className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-white shadow-lg px-2.5 py-1.5 text-sm font-mono text-gray-700 cursor-grabbing">
                        {activeDrag.course.courseInfo.subject} {activeDrag.course.courseInfo.number}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
